import { TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { PricelistQRSection } from "./PricelistQRSection";
import { resolveImageUrl } from "@/utils/imageUrl";

export function AdminContentTabs({
  events, news, gallery, pricelist, offers, reservations,
  isCentral, managedClubs, openDialog, handleDelete, handleCsvImport, csvUploading,
}) {
  return (
    <>
      {/* Events Tab */}
      <TabsContent value="events">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Termine</h2>
          <button onClick={() => openDialog("event", { club_id: managedClubs[0]?.id, featured: false, billard_available: true, edart_available: true, steeldart_available: true })}
            data-testid="add-event-btn" className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase">
            <Plus className="w-4 h-4" /> Neu
          </button>
        </div>
        <div className="space-y-2">
          {events.map(e => (
            <div key={e.id} className="bg-card border border-white/5 p-4 flex items-center justify-between hover:border-white/20 transition-colors duration-200" data-testid={`admin-event-${e.id}`}>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDialog("event", e, e.id)}>
                <p className="font-semibold text-sm truncate">{e.title}</p>
                <p className="text-xs text-muted-foreground">{e.date} | {e.club_id} {e.featured ? "| Featured" : ""}</p>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button onClick={() => openDialog("event", e, e.id)} data-testid={`edit-event-${e.id}`}
                  className="p-2 text-muted-foreground hover:text-foreground rounded"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete("events", e.id)} data-testid={`delete-event-${e.id}`}
                  className="p-2 text-destructive hover:bg-destructive/10 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </TabsContent>

      {/* News Tab */}
      <TabsContent value="news">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Neuigkeiten</h2>
          <button onClick={() => openDialog("news", { club_id: managedClubs[0]?.id })}
            data-testid="add-news-btn" className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase">
            <Plus className="w-4 h-4" /> Neu
          </button>
        </div>
        <div className="space-y-2">
          {news.map(n => (
            <div key={n.id} className="bg-card border border-white/5 p-4 flex items-center justify-between hover:border-white/20 transition-colors duration-200" data-testid={`admin-news-${n.id}`}>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDialog("news", n, n.id)}>
                <p className="font-semibold text-sm truncate">{n.title}</p>
                <p className="text-xs text-muted-foreground">{n.club_id} | {new Date(n.created_at).toLocaleDateString('de-AT')}</p>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button onClick={() => openDialog("news", n, n.id)} data-testid={`edit-news-${n.id}`}
                  className="p-2 text-muted-foreground hover:text-foreground rounded"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete("news", n.id)} data-testid={`delete-news-${n.id}`}
                  className="p-2 text-destructive hover:bg-destructive/10 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </TabsContent>

      {/* Gallery Tab */}
      <TabsContent value="gallery">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Galerie</h2>
          <button onClick={() => openDialog("gallery", { club_id: managedClubs[0]?.id })}
            data-testid="add-gallery-btn" className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase">
            <Plus className="w-4 h-4" /> Neu
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {gallery.map(g => {
            const imgs = g.image_urls?.length ? g.image_urls : (g.image_url ? [g.image_url] : []);
            const thumbUrl = imgs[0] || "";
            return (
              <div key={g.id} className="bg-card border border-white/5 relative group cursor-pointer hover:border-white/20 transition-colors duration-200" data-testid={`admin-gallery-${g.id}`}
                onClick={() => openDialog("gallery", { ...g, image_urls: imgs }, g.id)}>
                <div className="aspect-square overflow-hidden">
                  {thumbUrl && <img src={resolveImageUrl(thumbUrl)} alt={g.title} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                </div>
                <div className="p-2">
                  <p className="text-xs font-semibold truncate">{g.title}</p>
                  <p className="text-xs text-muted-foreground">{g.club_id} {imgs.length > 1 ? `| ${imgs.length} Bilder` : ""}</p>
                </div>
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button onClick={(ev) => { ev.stopPropagation(); openDialog("gallery", { ...g, image_urls: imgs }, g.id); }}
                    className="p-1 bg-background/80 rounded"><Pencil className="w-3 h-3 text-foreground" /></button>
                  <button onClick={(ev) => { ev.stopPropagation(); handleDelete("gallery", g.id); }}
                    className="p-1 bg-destructive/80 rounded"><Trash2 className="w-3 h-3 text-white" /></button>
                </div>
              </div>
            );
          })}
        </div>
      </TabsContent>

      {/* Pricelist Tab */}
      {isCentral && (
        <TabsContent value="pricelist">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Getränkekarte</h2>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 px-4 py-2 bg-card border border-white/10 text-xs font-bold uppercase cursor-pointer hover:bg-white/5 transition-colors"
                data-testid="csv-import-btn">
                <Upload className="w-4 h-4" /> {csvUploading ? "Importiert..." : "CSV Import"}
                <input type="file" accept=".csv" onChange={handleCsvImport} className="hidden" disabled={csvUploading} />
              </label>
              <button onClick={() => openDialog("pricelist", { category: "drinks", sort_order: 0 })}
                data-testid="add-pricelist-btn" className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase">
                <Plus className="w-4 h-4" /> Neu
              </button>
            </div>
          </div>

          <PricelistQRSection />

          <div className="space-y-2">
            {pricelist.map(p => (
              <div key={p.id} className="bg-card border border-white/5 p-4 flex items-center justify-between hover:border-white/20 transition-colors duration-200" data-testid={`admin-price-${p.id}`}>
                <div className="flex-1 cursor-pointer" onClick={() => openDialog("pricelist", p, p.id)}>
                  <p className="font-semibold text-sm">{p.name} <span className="text-muted-foreground">({p.category})</span></p>
                  <p className="text-xs text-muted-foreground">{p.description}</p>
                </div>
                <span className="text-sm font-bold text-primary mx-4">{p.price.toFixed(2)} EUR</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => openDialog("pricelist", p, p.id)}
                    className="p-2 text-muted-foreground hover:text-foreground rounded"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete("pricelist", p.id)}
                    className="p-2 text-destructive hover:bg-destructive/10 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      )}

      {/* Special Offers Tab */}
      {isCentral && (
        <TabsContent value="offers">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>Angebote</h2>
            <button onClick={() => openDialog("offer", { active: true })}
              data-testid="add-offer-btn" className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground text-xs font-bold uppercase">
              <Plus className="w-4 h-4" /> Neu
            </button>
          </div>
          <div className="space-y-2">
            {offers.map(o => (
              <div key={o.id} className="bg-card border border-white/5 p-4 flex items-center justify-between hover:border-white/20 transition-colors duration-200" data-testid={`admin-offer-${o.id}`}>
                <div className="flex-1 cursor-pointer" onClick={() => openDialog("offer", o, o.id)}>
                  <p className="font-semibold text-sm">{o.title} {!o.active && <Badge variant="outline" className="text-xs ml-2">Inaktiv</Badge>}</p>
                  <p className="text-xs text-muted-foreground">{o.offer_price.toFixed(2)} EUR</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openDialog("offer", o, o.id)}
                    className="p-2 text-muted-foreground hover:text-foreground rounded"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete("special-offers", o.id)}
                    className="p-2 text-destructive hover:bg-destructive/10 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      )}

      {/* Reservations Tab */}
      <TabsContent value="reservations">
        <h2 className="text-xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald' }}>Reservierungen</h2>
        <div className="space-y-2">
          {reservations.map(r => (
            <div key={r.id} className="bg-card border border-white/5 p-4 flex items-center justify-between" data-testid={`admin-reservation-${r.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{r.name} - {r.resource_type} #{r.resource_number}</p>
                <p className="text-xs text-muted-foreground">{r.date} | {r.time_slot} | {r.email} | {r.phone}</p>
              </div>
              <Badge className={`text-xs ${r.status === 'confirmed' ? 'bg-green-600' : r.status === 'cancelled' ? 'bg-red-600' : 'bg-yellow-600'}`}>
                {r.status === 'confirmed' ? 'Bestätigt' : r.status === 'cancelled' ? 'Storniert' : 'Ausstehend'}
              </Badge>
            </div>
          ))}
          {reservations.length === 0 && <p className="text-sm text-muted-foreground">Keine Reservierungen vorhanden.</p>}
        </div>
      </TabsContent>
    </>
  );
}
